import React from 'react';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import './SegmentedBarCard.css';

/**
 * SegmentedBarCard component.
 *
 * Visualises a set of categories as a single stacked horizontal bar — the
 * "unfolded donut" equivalent of PieChartCard. Each segment is coloured
 * proportionally to its share of the total.
 *
 * Layout:
 *   Row 1 — bold card title
 *   Row 2 — full-width segmented bar  +  primary-segment % on the right
 *   Row 3 — small inline labels: coloured dot · name · count
 *
 * @component
 * @param {object}  props
 * @param {string}  props.title           - Card title.
 * @param {Array<{
 *   id:    number,
 *   value: number,
 *   label: string,
 *   color: string,
 *   description?: string
 * }>} [props.data=[]]                    - Segment descriptors (same shape as PieChartCard).
 * @param {boolean} [props.isEmpty=false] - Renders a gray placeholder bar when `true`.
 * @param {boolean} [props.loading=false] - Shows a shimmer skeleton when `true`.
 * @returns {JSX.Element}
 */
const SegmentedBarCard = ({
    title,
    data = [],
    isEmpty = false,
    loading = false,
}) => {
    const total      = isEmpty || data.length === 0 ? 0 : data.reduce((sum, item) => sum + item.value, 0);
    const getPercent = (value) => total > 0 ? Math.round((value / total) * 100) : 0;
    const { t } = useTranslation();

    // The percentage shown to the right of the bar is that of the first (primary) segment.
    const primaryPercent = data.length > 0 ? getPercent(data[0].value) : 0;

    return (
        <div className="sbc-card">

            {/* ── Row 1: title ── */}
            <div className="sbc-header">
                <span className="sbc-title">{title}</span>
            </div>

            {loading ? (
                <div className="sbc-skeleton" />
            ) : (
                <>
                    {/* ── Row 2: segmented bar + primary % ── */}
                    <div className="sbc-bar-row">
                        <div className="sbc-bar">
                            {isEmpty || total === 0 ? (
                                <div className="sbc-segment" style={{ width: '100%', backgroundColor: '#e5e7eb' }} />
                            ) : (
                                data.map((item) => {
                                    const pct = getPercent(item.value);
                                    return pct > 0 ? (
                                        <Tooltip
                                            key={item.id}
                                            title={`${item.label}: ${pct}%`}
                                            placement="top"
                                            arrow
                                        >
                                            <div
                                                className="sbc-segment"
                                                style={{ width: `${pct}%`, backgroundColor: item.color }}
                                            />
                                        </Tooltip>
                                    ) : null;
                                })
                            )}
                        </div>
                        {!isEmpty && total > 0 && (
                            <div className="sbc-primary-pct">
                                <span className="sbc-primary-value">{primaryPercent}%</span>
                                <span className="sbc-primary-sub">{t('Total')}</span>
                            </div>
                        )}
                    </div>

                    {/* ── Row 3: category labels ── */}
                    {!isEmpty && total > 0 && (
                        <div className="sbc-legend">
                            {data.map((item) => (
                                <div key={item.id} className="sbc-legend-item">
                                    <span className="sbc-legend-label">
                                        <strong>{item.value} {item.label}</strong>
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

        </div>
    );
};

export default SegmentedBarCard;
